import { create } from 'zustand';
import type { User } from '@/api/types/auth.types';

interface AuthStore {
  user: User | null;
  accessToken: string | null;
  roles: string[];
  tenantId: string | null;
  entitledModules: string[];
  isAuthenticated: boolean;
  isInitialized: boolean;

  // Actions
  login: (accessToken: string, user: User) => void;
  logout: () => void;
  updateUser: (patch: Partial<User>) => void;
  setAccessToken: (accessToken: string) => void;
  setInitialized: () => void;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  accessToken: null,
  roles: [],
  tenantId: null,
  entitledModules: [],
  isAuthenticated: false,
  isInitialized: false,

  login: (accessToken, user) => {
    // Refresh token is in httpOnly cookie — not stored in JS
    set({
      accessToken,
      user,
      roles: user.roles,
      tenantId: user.tenantId,
      entitledModules: user.entitledModules ?? [],
      isAuthenticated: true,
    });
  },

  logout: () => {
    set({
      accessToken: null,
      user: null,
      roles: [],
      tenantId: null,
      entitledModules: [],
      isAuthenticated: false,
    });
  },

  // Merge a partial patch into the current user (e.g. profile save) so store readers
  // (Header, Sidebar) reflect the change without a full reload. No-op if no user.
  updateUser: (patch) => {
    const current = get().user;
    if (current) set({ user: { ...current, ...patch } });
  },

  setAccessToken: (accessToken) => {
    set({ accessToken, isAuthenticated: true });
  },

  setInitialized: () => {
    set({ isInitialized: true });
  },

  hasRole: (role) => get().roles.includes(role),

  hasAnyRole: (roles) => roles.some((r) => get().roles.includes(r)),
}));
